import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  TabMenu,
  TabMenuList,
  TabMenuTrigger,
} from '@ads/components-react';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { usePlanEdit } from '../../../context/PlanEditContext';

/** Abas da tela de criação de plano. */
export const PLAN_CREATE_TABS = ['detalhes', 'valores'] as const;

/** Aba ativa da tela de criação de plano. */
export type PlanCreateTab = (typeof PLAN_CREATE_TABS)[number];

/** Props de `PlanCreateTemplate`. */
export interface PlanCreateTemplateProps {
  /** Aba ativa. */
  readonly activeTab: PlanCreateTab;
  /** Recebe a aba escolhida. */
  readonly onTabChange: (tab: PlanCreateTab) => void;
  /**
   * Bloco de vínculo — renderizado acima das abas.
   *
   * Fica fora delas porque é o contexto que define o conteúdo das duas: as
   * formas de pagamento e as parcelas dependem do plano escolhido aqui. Dentro
   * de uma aba, o operador poderia estar preenchendo Valores sem enxergar o
   * vínculo que os condiciona — e sem ver os erros dos três campos obrigatórios.
   */
  readonly linkSection: ReactNode;
  /** Conteúdo da aba ativa. */
  readonly children: ReactNode;
  /** Dispara a criação. */
  readonly onSave: () => void;
  /** Descarta e volta para a listagem. */
  readonly onCancel: () => void;
}

const TAB_LABEL_KEY = {
  detalhes: 'planEdit.tabs.details',
  valores: 'planEdit.tabs.values',
} as const satisfies Record<PlanCreateTab, string>;

/**
 * Casca da tela de criação de plano financeiro.
 *
 * Espelha a edição — vínculo acima, abas, régua Cancelar/Salvar fora do painel —
 * sem o que pressupõe um contrato já existente: não há card de contrato (o
 * vínculo ainda está sendo escolhido), não há bloco de auditoria (não há
 * histórico) e não há aba de Documentos de Convênio (pertencem a um contrato
 * criado).
 *
 * A régua fica fora das abas pelo mesmo motivo da edição: é um `POST` só para as
 * duas, então as ações não pertencem a nenhuma delas em particular.
 */
export function PlanCreateTemplate({
  activeTab,
  onTabChange,
  linkSection,
  children,
  onSave,
  onCancel,
}: PlanCreateTemplateProps) {
  const { t } = useTranslation('individualPayment');
  const { isSaving, isReadOnly } = usePlanEdit();

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-col gap-mini">
        {/* Só o último segmento é `BreadcrumbPage`: quem emite o ">" é o
            `BreadcrumbLink`, então um `BreadcrumbPage` no meio engole o separador. */}
        <Breadcrumb>
          <BreadcrumbLink asChild>
            <Link to="/">
              <BreadcrumbIcon />
            </Link>
          </BreadcrumbLink>
          <BreadcrumbLink asChild>
            <Link to="/individual-payment">{t('planEdit.breadcrumb.root')}</Link>
          </BreadcrumbLink>
          <BreadcrumbPage>{t('planCreate.breadcrumb.current')}</BreadcrumbPage>
        </Breadcrumb>

        <div className="flex flex-col gap-quark">
          <h1 className="ts-heading-md text-ctx-content-title">{t('planCreate.title')}</h1>
          <p className="ts-paragraph-xs text-ctx-content-placeholder">{t('planCreate.subtitle')}</p>
        </div>
      </div>

      {linkSection}

      <TabMenu value={activeTab} onValueChange={value => onTabChange(value as PlanCreateTab)}>
        <TabMenuList>
          {PLAN_CREATE_TABS.map(tab => (
            <TabMenuTrigger key={tab} value={tab}>
              {t(TAB_LABEL_KEY[tab])}
            </TabMenuTrigger>
          ))}
        </TabMenuList>
      </TabMenu>

      {children}

      <div className="flex flex-wrap items-center justify-end gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-mini">
        <span className="flex items-center gap-micro">
          <Button hierarchy="secondary" size="md" disabled={isSaving} onClick={onCancel}>
            {t('planCreate.footer.cancel')}
          </Button>
          <Button
            hierarchy="primary"
            size="md"
            isLoading={isSaving}
            disabled={isSaving || isReadOnly}
            onClick={onSave}
          >
            {isSaving ? t('planCreate.footer.saving') : t('planCreate.footer.save')}
          </Button>
        </span>
      </div>
    </div>
  );
}
